import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../contexts/NotificationsContext';
import './NotificationBell.css';

const variantLabel = (variant: string) => {
  if (variant === 'lower_green') return 'Pharaon';
  if (variant === 'sedmica') return 'Sedmica';
  if (variant === 'raub') return 'Raub';
  return variant;
};

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { friendRequests, gameInvites, totalCount, acceptFriend, rejectFriend, dismissInvite } =
    useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const handleJoinInvite = (gameId: string, inviteId: string) => {
    dismissInvite(inviteId);
    setOpen(false);
    navigate(`/games/${gameId}`);
  };

  return (
    <div className="notif-bell" ref={ref}>
      <button
        className="notif-bell-btn"
        aria-label="Notifications"
        onClick={() => setOpen(o => !o)}
      >
        <span className="notif-bell-icon">🔔</span>
        {totalCount > 0 && (
          <span className="notif-bell-badge">{totalCount > 99 ? '99+' : totalCount}</span>
        )}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-panel-header">
            <span>{t('notifications.panelTitle')}</span>
            <span className="notif-panel-count">{totalCount}</span>
          </div>

          <div className="notif-panel-body">
            {totalCount === 0 && (
              <div className="notif-empty">
                <div className="notif-empty-icon">🔕</div>
                <p>{t('notifications.allCaughtUp')}</p>
              </div>
            )}

            {gameInvites.length > 0 && (
              <div className="notif-group">
                <div className="notif-group-title">🎮 {t('notifications.gameInvitationsGroup')}</div>
                {gameInvites.map(invite => (
                  <div key={invite.id} className="notif-item">
                    <div className="notif-item-main">
                      <div className="notif-item-title">
                        {invite.from.firstName} {invite.from.lastName}
                      </div>
                      <div className="notif-item-sub">
                        {t('notifications.invitedToPlay', { game: variantLabel(invite.gameVariant) })}
                      </div>
                    </div>
                    <div className="notif-item-actions">
                      <button
                        className="notif-btn notif-btn-accept"
                        onClick={() => handleJoinInvite(invite.gameId, invite.id)}
                      >
                        {t('notifications.join')}
                      </button>
                      <button
                        className="notif-btn notif-btn-reject"
                        onClick={() => dismissInvite(invite.id)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {friendRequests.length > 0 && (
              <div className="notif-group">
                <div className="notif-group-title">👥 {t('notifications.friendRequestsGroup')}</div>
                {friendRequests.map(req => (
                  <div key={req.id} className="notif-item">
                    <div className="notif-item-main">
                      <div className="notif-item-title">
                        {req.user.firstName} {req.user.lastName}
                      </div>
                      <div className="notif-item-sub">{t('notifications.wantsToBeFriend')}</div>
                    </div>
                    <div className="notif-item-actions">
                      <button
                        className="notif-btn notif-btn-accept"
                        onClick={() => acceptFriend(req.user.id)}
                      >
                        {t('notifications.accept')}
                      </button>
                      <button
                        className="notif-btn notif-btn-reject"
                        onClick={() => rejectFriend(req.user.id)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {totalCount > 0 && (
            <button
              className="notif-panel-footer"
              onClick={() => {
                setOpen(false);
                navigate('/invitations');
              }}
            >
              {t('notifications.viewAll')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
